// Raw three.js based 3D column view ("real 3D" rendering option).
//
// Deliberately implemented with the imperative three.js API (no @react-three/fiber / drei).
// react-three-fiber augments the global JSX namespace with hundreds of intrinsic elements
// (<mesh>, <boxGeometry>, ...) which was found to blow up TypeScript's union-type inference
// in unrelated files elsewhere in this codebase (TS2590 "union type too complex" in
// SXModal.tsx / LCOGeneratorModal.tsx, which use conditional JSX expressions). Using three.js
// directly avoids that global augmentation while still satisfying the "real 3D via three.js"
// requirement.
//
// The camera direction is fixed to a standard isometric projection and never rotates — there is
// intentionally no OrbitControls here. It does pan and zoom, but only as a flat screen-space
// transform driven by usePanZoom (and only while auto zoom is switched off), so the projection
// itself is unchanged.
import * as React from "react";
import * as THREE from "three";
import { ng } from "@common/ngds";
import { useProject } from "../../features/projects/projectSlice";
import { useSelector } from "react-redux";
import {
  selectShow3dCardboard,
  selectScale3dX,
  selectScale3dYZ,
  selectCardboardDistance3d,
  selectResultBoardLaneWidth3d,
  selectRightHand3d,
  selectResultBoardGraphAmplitude3d,
  selectResultBoardClearance3d,
  selectResultBoardHeaderOffsetsLeft3d,
  selectResultBoardHeaderOffsetsRight3d,
  selectOffsetLoadsFromColumn3d,
  selectStartCardboardsAtOuterCenterLine3d,
  selectShowSectionDimensions,
  selectShow3dLoadsOnOppositeSide,
  selectShow3dReinforcement,
  selectShowSectionBuckling,
  selectLoadColumnSpacing3d,
  selectGeometryDebug3d,
  selectDebugExtrusion3d,
  selectDebugExtrusionNormals3d,
  selectDebugExtrusionNormalsCenter3d,
  selectDebugExtrusionNormalsRadius3d,
  selectDrawFaces3d,
  selectShadeFaces3d,
  selectLightYaw3d,
  selectLightPitch3d,
  selectHiddenLines3d,
  selectHiddenLineGrey3d,
  selectShadeLighterTimberPercent3d,
  selectShadeLighterPercent3d,
  selectShadeDarkerPercent3d,
} from "../../features/ui/uiSlice";
import {
  calculateGeomCrossSection,
  getGeomCrossSectionPoints,
  shouldUseFakeSilhouetteForSection,
} from "./drawGeomCrossSection";
import {
  FACE_SIDE_COLOR,
  dot,
  getExtrusionCapNormal,
  getExtrusionFaceNormals,
  ISOMETRIC_VIEW_DIRECTION,
  ngLightDirection,
  mixOklab,
  selectExtrusionEdges,
  selectExtrusionFaceNormalsForDebug,
  shadeFaceColor,
  shadeFactor,
  threeFromNgDirection,
} from "./extrusionEdges";
import {
  classifyMeshEdges,
  scalePlacedMesh,
  transformMesh,
  triangulateFan,
  visibleMeshFaces,
} from "./mesh";
import { collectSupportMeshInstances, MeshInstance } from "./supportMeshes";
import { calculate_column_side_geom_size } from "./drawSideGeometry";
import { columnLighterPercent, columnMaterialFill } from "./shared";
import { GraphDirection } from "./SideView";
import {
  DiagramHeaderRun,
  DiagramResults,
  CreateGraphValues,
  isometricDiagramValueRanges,
  layoutDiagramHeader,
} from "./drawSectionForceDiagrams";
import type { GraphPointsMode } from "../Viewport";
import { create_buckling_data } from "./drawBucklingData";
import { get_loads, classify_direction, force_from_point_load, getSelectedLoadcaseGuids } from "./drawLoads";
import { loadcases_getColor } from "../../helpers/Colors";
import { getStandaloneBars, REBAR_DIAMETER_MM_TO_RADIUS_M } from "./drawLongitudalReinf";
import { diagram_header_style, isometric_3d } from "../ViewportStyles";
import type { Point } from "@common/components/Rendering";
import { usePanZoom, PanZoomResetButton, ViewportButtons, type ViewTransform } from "@common/components/Rendering";
import { useAutoZoomViewport } from "../../features/user/userSlice";
import {
  RESULT_BOARD_LANE_COUNT,
  ResultBoardDiagramType,
  resultBoardLaneIndex,
  resultBoardLoadBase,
  loadCaseLanes,
  eccentricityOffsetPath,
  resultBoardWidth,
  resultBoardValueLabels,
  resultBoardScreenSide,
} from "./resultBoard";

// the 2D side view's header font size, so the result board headers match it
const diagramHeaderFontSize = typeof diagram_header_style.textSize === "number" ? diagram_header_style.textSize : 12;

interface View3DProps {
  results: DiagramResults;
  selectedLcombGuid: string;
  graphPointsMode: GraphPointsMode;
  onAxisOriginScreenChange?: (value: { x: number; y: number }) => void;
}

// standard isometric angles: rotate 45 degrees around the vertical (world x, "up") axis, then
// tilt by atan(1/sqrt(2)) (~35.264deg) — this is the fixed, non-interactive camera placement.
const ISO_YAW = Math.PI / 4;
const ISO_PITCH = Math.atan(1 / Math.sqrt(2));

const resolveCssColor = (color: string): string => {
  if (typeof window === "undefined") return "#888";
  const probe = document.createElement("span");
  probe.style.color = color;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return resolved || "#888";
};

// Note: in three.js world space we map ng's "x-up" convention directly to three's Y-up axis, i.e.
// world.y = ng.x (column height), world.x = ng.y, world.z = ng.z. This keeps the isometric camera
// math (which rotates around the vertical axis) simple, using three's built-in Y-up convention.
export const ThreeColumnView = (props: View3DProps) => {
  const autoZoom = useAutoZoomViewport();
  const panZoom = usePanZoom(!autoZoom);
  const containerRef = panZoom.ref;
  const contentChanged = panZoom.contentChanged;
  const { project: projectSource } = useProject();
  const scaleX = useSelector(selectScale3dX);
  const scaleYZ = useSelector(selectScale3dYZ);
  const cardboardDistance = useSelector(selectCardboardDistance3d);
  const resultBoardLaneWidth = useSelector(selectResultBoardLaneWidth3d);
  const rightHand = useSelector(selectRightHand3d);
  const resultBoardGraphAmplitude = useSelector(selectResultBoardGraphAmplitude3d);
  const resultBoardClearance = useSelector(selectResultBoardClearance3d);
  const resultBoardHeaderOffsetsLeft = useSelector(selectResultBoardHeaderOffsetsLeft3d);
  const resultBoardHeaderOffsetsRight = useSelector(selectResultBoardHeaderOffsetsRight3d);
  const offsetLoadsFromColumn = useSelector(selectOffsetLoadsFromColumn3d);
  const startCardboardsAtOuterCenterLine = useSelector(selectStartCardboardsAtOuterCenterLine3d);
  const show3dLoadsOnOppositeSide = useSelector(selectShow3dLoadsOnOppositeSide);
  const showDimensions = useSelector(selectShowSectionDimensions);
  const showCardboard = useSelector(selectShow3dCardboard);
  const showReinforcement = useSelector(selectShow3dReinforcement);
  const showBuckling = useSelector(selectShowSectionBuckling);
  const loadColumnSpacing = useSelector(selectLoadColumnSpacing3d);
  const geometryDebug3d = useSelector(selectGeometryDebug3d);
  const debugExtrusion3d = useSelector(selectDebugExtrusion3d);
  const debugExtrusionNormals3d = useSelector(selectDebugExtrusionNormals3d);
  const debugExtrusionNormalsCenter3d = useSelector(selectDebugExtrusionNormalsCenter3d);
  const debugExtrusionNormalsRadius3d = useSelector(selectDebugExtrusionNormalsRadius3d);
  const drawFaces3d = useSelector(selectDrawFaces3d);
  const shadeFaces3d = useSelector(selectShadeFaces3d);
  const lightYaw3d = useSelector(selectLightYaw3d);
  const lightPitch3d = useSelector(selectLightPitch3d);
  // ng space for meshes carrying their own ng normals (supports), world space for the column
  const ngLight = React.useMemo(() => ngLightDirection(lightYaw3d, lightPitch3d), [lightYaw3d, lightPitch3d]);
  const worldLight = React.useMemo(() => threeFromNgDirection(ngLight), [ngLight]);
  const hiddenLines3d = useSelector(selectHiddenLines3d);
  const hiddenLineGrey3d = useSelector(selectHiddenLineGrey3d);
  const shadeLighterTimberPercent3d = useSelector(selectShadeLighterTimberPercent3d);
  const shadeLighterPercent3d = useSelector(selectShadeLighterPercent3d);
  const shadeDarkerPercent3d = useSelector(selectShadeDarkerPercent3d);

  // memoized so the useEffect below (which tears down/rebuilds the whole WebGL scene) only
  // re-runs when the underlying project data actually changes, not on every unrelated re-render
  const projectData = React.useMemo(
    () => ng.frontend.column.Project.fromObject(projectSource?.data),
    [projectSource?.data],
  );
  const columnSideColor = resolveCssColor(columnMaterialFill(projectData));
  const lighterPercent = columnLighterPercent(projectData, shadeLighterTimberPercent3d, shadeLighterPercent3d);
  const columnTopColor = mixOklab(columnSideColor, "#ffffff", lighterPercent / 100);
  const columnBottomColor = mixOklab(columnSideColor, "#000000", shadeDarkerPercent3d / 100);
  const shadedColumnColor = React.useCallback(
    (normal: { x: number; y: number; z: number }, lightDirection: { x: number; y: number; z: number }) =>
      mixOklab(columnBottomColor, columnTopColor, shadeFactor(normal, lightDirection)),
    [columnBottomColor, columnTopColor],
  );
  const fakeSilhouette3d = shouldUseFakeSilhouetteForSection(projectData);

  const geomExtent = projectData ? calculateGeomCrossSection(projectData) : null;
  const { size: sizeY, height: column_height } = calculate_column_side_geom_size(projectData, "xy");
  const { size: sizeZ } = calculate_column_side_geom_size(projectData, "xz");
  const halfY =
    ((Number.isFinite(sizeY) && sizeY > 0 ? sizeY : geomExtent ? geomExtent.hor.max - geomExtent.hor.min : 0.3) / 2) *
    scaleYZ;
  const halfZ =
    ((Number.isFinite(sizeZ) && sizeZ > 0 ? sizeZ : geomExtent ? geomExtent.ver.max - geomExtent.ver.min : 0.3) / 2) *
    scaleYZ;
  const height = (Number.isFinite(column_height) && column_height > 0 ? column_height : 3) * scaleX;
  // see loadBoardNear in SvgIsometricColumnView.tsx
  const loadBoardNear = (currDir: GraphDirection) => {
    const halfYUnscaled = halfY / scaleYZ;
    const halfZUnscaled = halfZ / scaleYZ;
    if (!offsetLoadsFromColumn)
      return resultBoardLoadBase(currDir === "xy" ? halfYUnscaled : halfZUnscaled, cardboardDistance);
    const boardPlane = startCardboardsAtOuterCenterLine ? 0 : -(currDir === "xy" ? halfZUnscaled : halfYUnscaled);
    return halfYUnscaled + halfZUnscaled - boardPlane + loadColumnSpacing / (Math.cos(Math.PI / 6) * scaleYZ);
  };

  // single uniform outline color for the column body, in classic CAD/construction-drawing style
  // (no per-material fill/shading) — matches the color used for the buckling curve/support markers.
  const outlineColor = resolveCssColor(isometric_3d.colors.outline);

  // the scene is rebuilt wholesale by the effect below, so the user's pan/zoom is handed to it
  // through a ref (to survive a rebuild without being a dependency of it) plus a re-apply hook
  // that the effect publishes for cheap view-only updates
  const viewRef = React.useRef<ViewTransform>(panZoom.view);
  viewRef.current = panZoom.view;
  const applyViewRef = React.useRef<(() => void) | null>(null);
  const hasBuiltOnce = React.useRef(false);
  React.useEffect(() => {
    applyViewRef.current?.();
  }, [panZoom.view]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth || 300;
    const heightPx = container.clientHeight || 300;

    const scene = new THREE.Scene();
    scene.background = null;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, heightPx);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    container.innerHTML = "";
    container.appendChild(renderer.domElement);
    // Mirror the rendered pixels, not the camera/geometry: face culling and shading stay unchanged.
    if (rightHand) renderer.domElement.style.transform = "scaleX(-1)";

    const textLabels: Array<{
      text: string;
      position: THREE.Vector3;
      color: string;
      fontSize: number;
      fontWeight: string;
      // laid-out runs (a diagram header, see layoutDiagramHeader); when set they replace text/color
      runs?: DiagramHeaderRun[];
    }> = [];
    const addTextLabel = (
      text: string,
      position: THREE.Vector3,
      color: string,
      fontSize = 12,
      fontWeight = "400",
      runs?: DiagramHeaderRun[],
    ) => {
      textLabels.push({ text, position, color, fontSize, fontWeight, runs });
    };

    // world-space mapping helper: ng (x up, y, z) -> three (x=y, y=x, z=z), scaled
    const toWorld = (x: number, y: number, z: number): THREE.Vector3 =>
      new THREE.Vector3(y * scaleYZ, x * scaleX, z * scaleYZ);

    const addLine = (points: THREE.Vector3[], color: string, dashed = false, opacity = 1, title?: string) => {
      if (points.length < 2) return;
      const geom = new THREE.BufferGeometry().setFromPoints(points);
      const mat = dashed
        ? new THREE.LineDashedMaterial({
            color,
            dashSize: isometric_3d.board.dashSize,
            gapSize: isometric_3d.board.dashGap,
            opacity,
            transparent: opacity < 1,
          })
        : new THREE.LineBasicMaterial({ color, opacity, transparent: opacity < 1 });
      const line = new THREE.Line(geom, mat);
      if (dashed) line.computeLineDistances();
      if (title) {
        line.name = title;
        line.userData.title = title;
      }
      scene.add(line);
      return line;
    };

    // An outline edge of the column or a support: drawn normally where nothing covers it, and in
    // the hidden-line style where a face does (GreaterDepth only passes behind something already
    // in the depth buffer, so the copy renders after the solids).
    const addOutlineEdge = (points: THREE.Vector3[], title?: string) => {
      addLine(points, outlineColor, false, 1, title);
      if (hiddenLines3d === "hide") return;
      const grey = hiddenLines3d === "grey" || hiddenLines3d === "dashedGrey";
      const dashed = hiddenLines3d === "dashed" || hiddenLines3d === "dashedGrey";
      const color = grey ? resolveCssColor(isometric_3d.colors.hiddenLine(hiddenLineGrey3d)) : outlineColor;
      const line = addLine(points, color, dashed, 1, title);
      if (!line) return;
      line.material.depthFunc = THREE.GreaterDepth;
      line.renderOrder = 1;
    };

    const addArrow = (from: THREE.Vector3, to: THREE.Vector3, color: string) => {
      const delta = to.clone().sub(from);
      const length = delta.length();
      if (length < 1e-9) return;
      const arrow = new THREE.ArrowHelper(
        delta.normalize(),
        from,
        length,
        new THREE.Color(color).getHex(),
        Math.min(isometric_3d.arrow.maximumHeadLength, length * isometric_3d.arrow.headLengthRatio),
        Math.min(isometric_3d.arrow.maximumHeadWidth, length * isometric_3d.arrow.headWidthRatio),
      );
      scene.add(arrow);
    };

    const createFaceMaterial = (color: string, opacity: number): THREE.MeshBasicMaterial =>
      new THREE.MeshBasicMaterial({
        color,
        opacity,
        transparent: opacity < 1,
        side: THREE.DoubleSide,
        depthWrite: opacity >= 1,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      });

    const addFilledQuad = (
      points: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3],
      color: string,
      opacity: number,
    ) => {
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      geometry.setIndex([0, 1, 2, 0, 2, 3]);
      scene.add(new THREE.Mesh(geometry, createFaceMaterial(color, opacity)));
    };

    // A face of the column or a support. With "draw faces" off it still writes depth, invisibly,
    // so it can hide outline edges behind it.
    const addSolidFace = (geometry: THREE.BufferGeometry, color: string) => {
      const material = createFaceMaterial(color, 1);
      if (!drawFaces3d) material.colorWrite = false;
      scene.add(new THREE.Mesh(geometry, material));
    };

    const addMarker = (pos: THREE.Vector3, color: string, wireframe: boolean, radius: number) => {
      const geom = new THREE.SphereGeometry(radius, 8, 8);
      const mat = new THREE.MeshBasicMaterial({ color, wireframe });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.copy(pos);
      scene.add(mesh);
    };

    // Faces (the column's side quads + top cap, and the support symbols' faces) are collected here
    // instead of being drawn immediately, so they can all be painted together in a single
    // back-to-front ("painter's algorithm") pass once every loop and every support has been
    // processed - even though three.js resolves per-pixel occlusion via its depth buffer
    // regardless of draw order, drawing furthest-first keeps coplanar/near-coplanar faces (e.g. a
    // hole's inner faces meeting the outer perimeter, or a support's plate meeting the column's
    // base) resolving consistently rather than relying on depth-test tie-breaking. It is one queue
    // for the whole solid scene, so the supports sort against the column rather than after it.
    const pendingFaces: { center: THREE.Vector3; draw: () => void }[] = [];

    // ISOMETRIC_VIEW_DIRECTION points towards the camera, so a *larger* dot product means closer:
    // sort ascending to paint furthest-first.
    const flushPendingFaces = () => {
      const viewDirection = new THREE.Vector3(
        ISOMETRIC_VIEW_DIRECTION.x,
        ISOMETRIC_VIEW_DIRECTION.y,
        ISOMETRIC_VIEW_DIRECTION.z,
      );
      const faces = [...pendingFaces];
      pendingFaces.length = 0;
      faces.sort((a, b) => a.center.dot(viewDirection) - b.center.dot(viewDirection)).forEach((face) => face.draw());
    };

    // Queues one entry of the render list below: the instance's constant mesh, moved and turned
    // into place, its front faces handed to the shared pass above and its outlines drawn along the
    // edges of the faces that survived.
    //
    // Visibility and shading read the mesh's *stored* normals, and do so in the shared ng space
    // (scaled), never in three's world space: toWorld swaps x and y, which mirrors the space and
    // would invert every normal recovered from winding - see the note at the top of mesh.ts.
    const addMeshInstance = (instance: MeshInstance) => {
      const placed = transformMesh(instance.mesh, instance);
      const viewSpace = scalePlacedMesh(placed, { x: scaleX, y: scaleYZ, z: scaleYZ });
      const visible = visibleMeshFaces(viewSpace, instance.mesh, ISOMETRIC_VIEW_DIRECTION);
      const worldPositions = placed.positions.map((position) => toWorld(position.x, position.y, position.z));

      for (const { face, normal } of visible) {
        const points = face.vertices.map((index) => worldPositions[index]);
        const color = shadeFaces3d ? shadeFaceColor(normal, ngLight) : FACE_SIDE_COLOR;
        const center = points
          .reduce((sum, p) => sum.add(p), new THREE.Vector3())
          .divideScalar(Math.max(1, points.length));
        pendingFaces.push({
          center,
          draw: () => {
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            geometry.setIndex(triangulateFan(points.length));
            addSolidFace(geometry, color);
          },
        });
      }

      // Only genuine shape features are outlined - a real corner (any edge touching a flat
      // primitive face) or an exact silhouette (a tessellation seam between two smooth/curved
      // facets whose visibility flips) - see classifyMeshEdges.
      const classified = classifyMeshEdges(instance.mesh, viewSpace, ISOMETRIC_VIEW_DIRECTION);
      for (const edge of classified) {
        if (!edge.keep) continue;
        addOutlineEdge([worldPositions[edge.from], worldPositions[edge.to]], debugExtrusion3d ? edge.debugInfo : undefined);
      }
    };

    // Render list for the solid support symbols: what mesh to draw, where, and how it is turned.
    // Built up front - before any of the column's own lines and faces are constructed - so the
    // supports are one plain list of placed meshes rather than per-type drawing code (see
    // supportMeshes.ts). Skipped in geometry-debug mode along with everything else that isn't the
    // column outline itself.
    const supportInstances: MeshInstance[] = geometryDebug3d
      ? []
      : collectSupportMeshInstances(projectData, { halfY: halfY / scaleYZ, halfZ: halfZ / scaleYZ });

    // column body: outline-only wireframe extrusion of the cross-section perimeter (and any
    // holes) along the member's height, in classic CAD/construction-drawing style (no filled
    // faces). Unlike the former plain box, the outline follows the actual section shape (e.g. a
    // circular or custom-polygon section is no longer flattened into its bounding box). A general
    // section outline is not always convex, so - unlike the former box - hidden-vs-visible edge
    // classification is not attempted here; every edge is drawn solid.
    {
      const safeColumnHeight = height / scaleX;
      const crossSection = projectData ? getGeomCrossSectionPoints(projectData) : null;

      const extrudeLoop = (points2d: Point[]) => {
        if (points2d.length < 2) return;
        const bottomPoints = points2d.map(([y, z]) => toWorld(0, y, z));
        const topPoints = points2d.map(([y, z]) => toWorld(safeColumnHeight, y, z));
        const keepVertical = selectExtrusionEdges(bottomPoints, topPoints, ISOMETRIC_VIEW_DIRECTION, fakeSilhouette3d);

        if (debugExtrusionNormals3d) {
          const normalLength = Math.max(0.05, Math.min(0.25, Math.max(halfY, halfZ) * 0.35)) * scaleYZ;
          const debugFaces = selectExtrusionFaceNormalsForDebug(
            getExtrusionFaceNormals(bottomPoints, topPoints, ISOMETRIC_VIEW_DIRECTION),
            debugExtrusionNormalsCenter3d,
            debugExtrusionNormalsRadius3d,
          );
          for (const face of debugFaces) {
            const color = face.isEdgeOn
              ? isometric_3d.colors.debugNormalEdgeOn
              : face.viewDot > 0
                ? isometric_3d.colors.debugNormalFront
                : isometric_3d.colors.debugNormalBack;
            const from = new THREE.Vector3(face.center.x, face.center.y, face.center.z);
            const to = from
              .clone()
              .add(new THREE.Vector3(face.normal.x, face.normal.y, face.normal.z).multiplyScalar(normalLength));
            addArrow(from, to, color);
            addTextLabel(`${face.fromIndex}->${face.toIndex}: ${face.viewDot.toFixed(3)}`, to, color, 10, "500");
          }
        }

        // Only faces facing the (fixed isometric) view direction are queued - the back side of
        // the extrusion is never visible from this camera, so filling it would just waste draw
        // calls and (being drawn with DoubleSide) could show through gaps in front-facing
        // geometry.
        const loopFaces = getExtrusionFaceNormals(bottomPoints, topPoints, ISOMETRIC_VIEW_DIRECTION);
        for (const face of loopFaces) {
          if (face.viewDot <= 0) continue;
          const i = face.fromIndex;
          const next = face.toIndex;
          const color = shadeFaces3d ? shadedColumnColor(face.normal, worldLight) : columnSideColor;
          pendingFaces.push({
            center: new THREE.Vector3(face.center.x, face.center.y, face.center.z),
            draw: () => {
              const geometry = new THREE.BufferGeometry().setFromPoints([
                bottomPoints[i],
                bottomPoints[next],
                topPoints[next],
                topPoints[i],
              ]);
              geometry.setIndex([0, 1, 2, 0, 2, 3]);
              addSolidFace(geometry, color);
            },
          });
        }

        for (let i = 0; i < points2d.length; i++) {
          const next = (i + 1) % points2d.length;
          addOutlineEdge([bottomPoints[i], bottomPoints[next]]);
          addOutlineEdge([topPoints[i], topPoints[next]]);
          if (keepVertical[i].keep) {
            addOutlineEdge([bottomPoints[i], topPoints[i]], debugExtrusion3d ? keepVertical[i].debugInfo : undefined);
          }
        }
      };

      if (crossSection) {
        extrudeLoop(crossSection.perimeter);
        for (const hole of crossSection.holes) {
          extrudeLoop(hole);
        }

        // top cap face - a single (possibly non-convex, possibly holed) polygon, so it can't be
        // drawn with addFilledQuad(); built as a flat THREE.Shape (in the world's x/z plane) with
        // the holes cut out, then rotated/translated up to the extrusion's top plane. Always
        // drawn (unlike side faces, which are culled when facing away from the viewer) since the
        // top cap is the column's top end and always faces the viewer.
        if (crossSection.perimeter.length > 0) {
          const toShapePoint = (p: THREE.Vector3) => new THREE.Vector2(p.x, p.z);
          const outerTopPoints = crossSection.perimeter.map(([y, z]) => toWorld(safeColumnHeight, y, z));
          const [firstY, firstZ] = crossSection.perimeter[0];
          const capNormal = getExtrusionCapNormal([toWorld(0, firstY, firstZ)], [outerTopPoints[0]]);

          pendingFaces.push({
            center: outerTopPoints
              .reduce((sum, p) => sum.add(p), new THREE.Vector3())
              .divideScalar(Math.max(1, outerTopPoints.length)),
            draw: () => {
              const shape = new THREE.Shape(outerTopPoints.map(toShapePoint));
              for (const hole of crossSection.holes) {
                if (hole.length === 0) continue;
                const holeTopPoints = hole.map(([y, z]) => toWorld(safeColumnHeight, y, z));
                shape.holes.push(new THREE.Path(holeTopPoints.map(toShapePoint)));
              }

              const capGeometry = new THREE.ShapeGeometry(shape);
              capGeometry.rotateX(Math.PI / 2);
              capGeometry.translate(0, toWorld(safeColumnHeight, 0, 0).y, 0);

              const capColor = shadeFaces3d
                ? shadedColumnColor(capNormal, worldLight)
                : columnTopColor;
              addSolidFace(capGeometry, capColor);
            },
          });
        }

        // Nothing is painted yet - the shared queue is flushed once, after the support symbols
        // have been queued too, so the whole solid scene sorts together.
      } else {
        // fallback: no section geometry available - draw a simple box outline sized from the
        // extent so the view is not left empty.
        const bottomY = 0;
        const topY = height;
        const C0 = new THREE.Vector3(-halfY, bottomY, -halfZ);
        const C1 = new THREE.Vector3(halfY, bottomY, -halfZ);
        const C2 = new THREE.Vector3(halfY, bottomY, halfZ);
        const C3 = new THREE.Vector3(-halfY, bottomY, halfZ);
        const C4 = new THREE.Vector3(-halfY, topY, -halfZ);
        const C5 = new THREE.Vector3(halfY, topY, -halfZ);
        const C6 = new THREE.Vector3(halfY, topY, halfZ);
        const C7 = new THREE.Vector3(-halfY, topY, halfZ);

        addLine([C0, C1], outlineColor);
        addLine([C1, C2], outlineColor);
        addLine([C2, C3], outlineColor);
        addLine([C3, C0], outlineColor);
        addLine([C4, C5], outlineColor);
        addLine([C5, C6], outlineColor);
        addLine([C6, C7], outlineColor);
        addLine([C7, C4], outlineColor);
        addLine([C0, C4], outlineColor);
        addLine([C1, C5], outlineColor);
        addLine([C2, C6], outlineColor);
        addLine([C3, C7], outlineColor);
      }
    }

    // The solid scene - column extrusion plus support symbols - is queued first and then painted
    // in one shared back-to-front pass, so the two sort against each other.
    for (const instance of supportInstances) addMeshInstance(instance);
    flushPendingFaces();

    // Loads, result diagrams/boards, buckling, reactions and supports are all skipped when
    // geometryDebug3d is enabled, leaving only the column outline (and reinforcement) visible -
    // useful for inspecting the extruded cross-section shape in isolation.

    // longitudinal reinforcement bars (concrete sections only): drawn the same as the SVG
    // isometric view - straight lines with small sphere markers at each end. Bar diameter is
    // stored in mm (unlike every other length here, which is meters), so it must go through
    // REBAR_DIAMETER_MM_TO_RADIUS_M before being scaled by scaleYZ.
    if (showReinforcement) {
      const standaloneBars = getStandaloneBars(projectData?.data ?? null);
      for (const bar of standaloneBars) {
        const start = bar.startPoint;
        const end = bar.endPoint;
        if (!start || !end) continue;
        const radius = bar.diameter * REBAR_DIAMETER_MM_TO_RADIUS_M * scaleYZ;
        const p3Start = toWorld(start.x, start.y, start.z);
        const p3End = toWorld(end.x, end.y, end.z);
        addLine([p3Start, p3End], outlineColor);
        addMarker(p3Start, outlineColor, false, radius);
        addMarker(p3End, outlineColor, false, radius);
      }
    }

    if (!geometryDebug3d) {
      // Axial loads act along the member's physical centerline, not on either result board.
      addLine([toWorld(0, 0, 0), toWorld(height / scaleX, 0, 0)], outlineColor, true, isometric_3d.board.opacity);

      // Full-height result boards along the member's XY and XZ planes.
      const graphTypes: ResultBoardDiagramType[] = ["M", "V", "N", "D"];
      // Fixed across every load combination, view direction and the 1st/2nd order toggle (see
      // isometricDiagramValueRanges) so the drawn curve size actually reflects the magnitude
      // instead of every curve independently filling the board.
      const sharedValueRanges = isometricDiagramValueRanges(
        props.results,
        props.graphPointsMode,
        projectData?.data?.output,
      );
      const diagramColor = (type: ResultBoardDiagramType): string =>
        type === "M"
          ? resolveCssColor(isometric_3d.colors.momentLine)
          : type === "V"
            ? resolveCssColor(isometric_3d.colors.shearLine)
            : type === "N"
              ? resolveCssColor(isometric_3d.colors.normalLine)
              : resolveCssColor(isometric_3d.colors.deformationLine);
      const diagramFill = (type: ResultBoardDiagramType): { color: string; opacity: number } | null => {
        if (type === "D") return null;
        return {
          color: resolveCssColor(
            type === "M"
              ? isometric_3d.colors.momentFill
              : type === "V"
                ? isometric_3d.colors.shearFill
                : isometric_3d.colors.normalFill,
          ),
          opacity: type === "M" || type === "V" ? isometric_3d.diagram.fillOpacity : 1,
        };
      };
      for (const currDir of ["xy", "xz"] as GraphDirection[]) {
        const half = currDir === "xy" ? halfY / scaleYZ : halfZ / scaleYZ;
        const oppositeDir: GraphDirection = currDir === "xy" ? "xz" : "xy";
        const loadPanelOuterOffset = (() => {
          if (!projectData) return loadBoardNear(oppositeDir);

          const { horizontal, line } = get_loads(projectData);
          const selectedLoadcaseGuids = getSelectedLoadcaseGuids(projectData, props.selectedLcombGuid);
          const isOppositeTransverseLoad = (load: ng.load.Data) => {
            if (!load.direction || !load.lcaseGuid || !selectedLoadcaseGuids.has(load.lcaseGuid)) return false;
            const direction = classify_direction(load.direction);
            return (direction === "y" ? "xy" : direction === "z" ? "xz" : null) === oppositeDir;
          };
          const loads = [...horizontal.filter(isOppositeTransverseLoad), ...line.filter(isOppositeTransverseLoad)];
          const magnitudes = [...horizontal, ...line]
            .filter((load) => {
              if (!load.direction || !load.lcaseGuid || !selectedLoadcaseGuids.has(load.lcaseGuid)) return false;
              return classify_direction(load.direction) !== "x";
            })
            .flatMap((load) => (line.includes(load) ? (load.values ?? []) : [force_from_point_load(load)]))
            .map(Math.abs)
            .filter(Number.isFinite);
          const horizontalScale =
            magnitudes.length > 0
              ? (isometric_3d.load.horizontalAmplitude - isometric_3d.load.minimumAmplitude) /
                Math.max(...magnitudes, 1e-9)
              : 0;
          const base = loadBoardNear(oppositeDir);
          const lanes = loadCaseLanes(
            loads,
            (load) =>
              isometric_3d.load.minimumAmplitude +
              (line.includes(load)
                ? Math.max(...(load.values ?? []).map(Math.abs), 0)
                : Math.abs(force_from_point_load(load))) * horizontalScale,
            isometric_3d.load.laneSpacing,
          );
          return base + lanes.outer;
        })();
        const boardNear = Math.max(
          half + cardboardDistance,
          loadPanelOuterOffset + resultBoardClearance / Math.cos(Math.PI / 6),
        );
        const boardFar = boardNear + resultBoardWidth(resultBoardLaneWidth);
        const boardPoint = (x: number, transverse: number): THREE.Vector3 =>
          currDir === "xy"
            ? toWorld(x, transverse, (startCardboardsAtOuterCenterLine ? 0 : -halfZ) / scaleYZ)
            : toWorld(x, (startCardboardsAtOuterCenterLine ? 0 : -halfY) / scaleYZ, transverse);
        const lcRes = props.results.elementForces[props.selectedLcombGuid];
        const elemResArr = lcRes ? Object.values(lcRes)[0] : null;
        const dispRes = props.results.displacements[props.selectedLcombGuid];
        const dispResArr = dispRes ? Object.values(dispRes)[0] : null;
        // The 1st order counterpart (App's showBothOrders), only present when both order toggles
        // are on at once - mirrors the 2D side view's elementForcesSecondary/displacementsSecondary.
        const secondaryLcRes = props.results.elementForcesSecondary?.[props.selectedLcombGuid];
        const secondaryElemResArr = secondaryLcRes ? Object.values(secondaryLcRes)[0] : null;
        const secondaryDispRes = props.results.displacementsSecondary?.[props.selectedLcombGuid];
        const secondaryDispResArr = secondaryDispRes ? Object.values(secondaryDispRes)[0] : null;
        const diagramValues = graphTypes.map((type) => ({
          type,
          values: CreateGraphValues(elemResArr ?? [], dispResArr ?? [], currDir, type, 1),
          secondaryValues: secondaryElemResArr
            ? CreateGraphValues(secondaryElemResArr, secondaryDispResArr ?? [], currDir, type, 1)
            : undefined,
        }));

        if (diagramValues.every(({ values }) => values.length === 0)) continue;

        if (showCardboard) {
          addLine(
            [
              boardPoint(0, boardNear),
              boardPoint(height / scaleX, boardNear),
              boardPoint(height / scaleX, boardFar),
              boardPoint(0, boardFar),
              boardPoint(0, boardNear),
            ],
            outlineColor,
            false,
            isometric_3d.board.opacity,
          );
        }

        for (let lane = 0; lane < RESULT_BOARD_LANE_COUNT; lane++) {
          const zeroOffset = boardNear + resultBoardLaneWidth * (lane + 0.5);
          addLine(
            [boardPoint(0, zeroOffset), boardPoint(height / scaleX, zeroOffset)],
            outlineColor,
            true,
            isometric_3d.board.opacity,
          );
        }

        // Draws one diagram curve (and its fill, if any) into the given lane. Shared by the primary
        // and, faded, by the 1st order overlay curve below.
        const drawDiagramCurve = (
          diagramType: ResultBoardDiagramType,
          curveValues: ReturnType<typeof CreateGraphValues>,
          zeroOffset: number,
          graphScale: number,
          color: string,
          fillOpacityScale: number,
          lineOpacity: number,
        ) => {
          const fill = diagramFill(diagramType);
          if (fill) {
            for (let i = 0; i + 1 < curveValues.length; i++) {
              const current = curveValues[i];
              const next = curveValues[i + 1];
              addFilledQuad(
                [
                  boardPoint(current.horizontalValue, zeroOffset),
                  boardPoint(current.horizontalValue, zeroOffset - current.verticalValue * graphScale),
                  boardPoint(next.horizontalValue, zeroOffset - next.verticalValue * graphScale),
                  boardPoint(next.horizontalValue, zeroOffset),
                ],
                fill.color,
                fill.opacity * fillOpacityScale,
              );
            }
          }
          const points = curveValues.map((v) => {
            const off = zeroOffset - v.verticalValue * graphScale;
            return boardPoint(v.horizontalValue, off);
          });
          addLine(points, color, false, lineOpacity);
        };

        for (const { type: diagramType, values, secondaryValues } of diagramValues) {
          if (values.length === 0) continue;
          // Shares one scale with the secondary curve (like the 2D side view) so the faded
          // reference curve stays inside its own lane rather than clamped against a narrower range.
          const combinedAbsValues = [...values, ...(secondaryValues ?? [])].map((v) => Math.abs(v.verticalValue));
          const localMaxAbs = Math.max(...combinedAbsValues, 1e-9);
          // See the comment on sharedValueRanges above; localMaxAbs is still a floor in case the
          // shared range is somehow stale.
          const sharedRange = sharedValueRanges[diagramType];
          const valueRange = sharedRange !== undefined && sharedRange >= localMaxAbs ? sharedRange : localMaxAbs;
          const effectiveValueRange = valueRange > 0 ? valueRange : 1;
          const graphScale = resultBoardGraphAmplitude / effectiveValueRange;
          const zeroOffset = boardNear + resultBoardLaneWidth * (resultBoardLaneIndex(diagramType) + 0.5);
          const color = diagramColor(diagramType);

          drawDiagramCurve(diagramType, values, zeroOffset, graphScale, color, 1, 1);

          // Overlay: the faded 1st order reference curve, same lane and scale as the curve just
          // drawn. No labels, no graph points - a visual comparison only. Three.js materials take
          // opacity directly, unlike the SVG view's color-mix strings.
          if (secondaryValues && secondaryValues.length > 0) {
            drawDiagramCurve(diagramType, secondaryValues, zeroOffset, graphScale, color, 0.25, 0.4);
          }

          for (const label of resultBoardValueLabels(values, diagramType, props.graphPointsMode)) {
            const graphOffset = zeroOffset - label.verticalValue * graphScale;
            const labelOffset =
              graphOffset +
              (label.verticalValue < 0 ? isometric_3d.diagram.labelOffset : -isometric_3d.diagram.labelOffset);
            const xOffset =
              diagramType === "M"
                ? isometric_3d.diagram.momentLabelXOffset
                : diagramType === "V"
                  ? -isometric_3d.diagram.shearLabelXOffset
                  : 0;
            const graphPoint = boardPoint(label.horizontalValue, graphOffset);
            addMarker(graphPoint, color, false, isometric_3d.diagram.pointRadius * scaleYZ);
            // tinted in the diagram's line colour, like the 2D side view, so M/V/N/D values stay apart
            addTextLabel(label.text, boardPoint(label.horizontalValue + xOffset, labelOffset), color);
          }
        }

        const availableTypes = new Set(diagramValues.filter(({ values }) => values.length > 0).map(({ type }) => type));
        const headerX = height / scaleX + isometric_3d.diagram.headerOffset;
        // the same header layout as the 2D side view: one segment per diagram, each in its own colour
        const addHeader = (lane: number, laneTypes: ResultBoardDiagramType[]) => {
          const types = laneTypes.filter((type) => availableTypes.has(type));
          if (types.length === 0) return;
          const sideOffsets =
            resultBoardScreenSide(currDir, rightHand) === "left"
              ? resultBoardHeaderOffsetsLeft
              : resultBoardHeaderOffsetsRight;
          const laneOffset = sideOffsets[lane];
          const runs = layoutDiagramHeader(
            types.map((type) => ({ type, color: diagramColor(type) })),
            currDir,
            diagramHeaderFontSize,
          );
          addTextLabel(
            runs.map((run) => run.text).join(""),
            boardPoint(headerX + laneOffset, boardNear + resultBoardLaneWidth * (lane + 0.5)),
            outlineColor,
            diagramHeaderFontSize,
            "400",
            runs,
          );
        };

        addHeader(0, ["N"]);
        addHeader(1, ["M", "V"]);
        addHeader(2, ["D"]);
      }

      // buckling deformed centerline
      const mode = props.results.bucklingMode?.[1] ?? [];
      const bucklingData = create_buckling_data(true, height / scaleX, mode, false);
      if (showBuckling && bucklingData.length > 0) {
        const maxVal = Math.max(...bucklingData.map((b) => Math.abs(b.value)), 1e-9);
        const bucklingPoints = bucklingData.map((b) => {
          const off =
            halfY / scaleYZ + isometric_3d.buckling.offset + (b.value / maxVal) * isometric_3d.buckling.amplitude;
          return toWorld(b.at, off, 0);
        });
        addLine(bucklingPoints, outlineColor, true);
      }

      // Loads occupy the first lane of each rear result board, between the column and result curves.
      if (projectData) {
        const { vertical, horizontal, line } = get_loads(projectData);
        const selectedLoadcaseGuids = getSelectedLoadcaseGuids(projectData, props.selectedLcombGuid);
        const isSelected = (load: ng.load.Data) => (load.lcaseGuid ? selectedLoadcaseGuids.has(load.lcaseGuid) : false);
        const selectedHorizontal = horizontal.filter((load) => isSelected(load) && load.direction);
        const selectedLine = line.filter(
          (load) => isSelected(load) && load.direction && classify_direction(load.direction) !== "x",
        );
        const selectedAxialLine = line.filter(
          (load) => isSelected(load) && load.direction && classify_direction(load.direction) === "x",
        );
        const horizontalMagnitudes = [
          ...selectedHorizontal.map(force_from_point_load),
          ...selectedLine.flatMap((load) => load.values ?? []),
        ]
          .map(Math.abs)
          .filter(Number.isFinite);
        const horizontalScale =
          horizontalMagnitudes.length > 0
            ? (isometric_3d.load.horizontalAmplitude - isometric_3d.load.minimumAmplitude) /
              Math.max(...horizontalMagnitudes, 1e-9)
            : 0;
        const selectedVertical = vertical.filter(isSelected);
        const verticalMagnitudes = [
          ...selectedVertical.map(force_from_point_load),
          ...selectedAxialLine.flatMap((load) => load.values ?? []),
        ]
          .map(Math.abs)
          .filter(Number.isFinite);
        const verticalScale =
          verticalMagnitudes.length > 0
            ? (isometric_3d.load.verticalAmplitude - isometric_3d.load.minimumAmplitude) /
              Math.max(...verticalMagnitudes, 1e-9)
            : 0;
        const boardPoint = (currDir: GraphDirection, x: number, transverse: number): THREE.Vector3 =>
          currDir === "xy"
            ? toWorld(x, transverse, (startCardboardsAtOuterCenterLine ? 0 : -halfZ) / scaleYZ)
            : toWorld(x, (startCardboardsAtOuterCenterLine ? 0 : -halfY) / scaleYZ, transverse);
        const transverseLoads = [...selectedHorizontal, ...selectedLine];
        const lanesFor = (currDir: GraphDirection) =>
          loadCaseLanes(
            transverseLoads.filter((candidate) => {
              if (!candidate.direction) return false;
              const direction = classify_direction(candidate.direction);
              return (direction === "y" ? "xy" : direction === "z" ? "xz" : null) === currDir;
            }),
            (load) =>
              isometric_3d.load.minimumAmplitude +
              (selectedLine.includes(load)
                ? Math.max(...(load.values ?? []).map(Math.abs), 0)
                : Math.abs(force_from_point_load(load))) * horizontalScale,
            isometric_3d.load.laneSpacing,
          );
        const lanes = { xy: lanesFor("xy"), xz: lanesFor("xz") };
        const loadBase = (currDir: GraphDirection, load: ng.load.Data) => {
          const side = show3dLoadsOnOppositeSide ? -1 : 1;
          return side * (loadBoardNear(currDir) + lanes[currDir].offsetOf(load));
        };
        const loadFar = (base: number, value: number) =>
          base +
          (show3dLoadsOnOppositeSide ? -1 : 1) *
            (isometric_3d.load.minimumAmplitude + Math.abs(value) * horizontalScale);
        const loadLabelOffset = show3dLoadsOnOppositeSide
          ? -isometric_3d.load.labelOffset
          : isometric_3d.load.labelOffset;
        if (showCardboard && show3dLoadsOnOppositeSide) {
          for (const currDir of ["xy", "xz"] as GraphDirection[]) {
            const loads = transverseLoads.filter((load) => {
              if (!load.direction) return false;
              const direction = classify_direction(load.direction);
              return (direction === "y" ? "xy" : direction === "z" ? "xz" : null) === currDir;
            });
            if (loads.length === 0) continue;

            const boardNear = loadBoardNear(currDir);
            const boardFar = Math.max(
              boardNear,
              ...loads.map((load) => {
                const value = selectedLine.includes(load)
                  ? Math.max(...(load.values ?? []).map(Math.abs), 0)
                  : Math.abs(force_from_point_load(load));
                return Math.abs(loadFar(loadBase(currDir, load), value)) + isometric_3d.board.loadPadding;
              }),
            );
            addLine(
              [
                boardPoint(currDir, 0, -boardNear),
                boardPoint(currDir, height / scaleX, -boardNear),
                boardPoint(currDir, height / scaleX, -boardFar),
                boardPoint(currDir, 0, -boardFar),
                boardPoint(currDir, 0, -boardNear),
              ],
              outlineColor,
              false,
              isometric_3d.board.opacity,
            );
          }
        }

        for (const load of selectedHorizontal) {
          if (!load.direction) continue;
          const dc = classify_direction(load.direction);
          if (dc === "x") continue;
          const currDir: GraphDirection = dc === "y" ? "xy" : "xz";
          const pos = load.positions?.[0];
          const x = pos?.x ?? 0;
          const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
          const force = force_from_point_load(load);
          if (!Number.isFinite(force) || Math.abs(force) < 1e-9) continue;
          const base = loadBase(currDir, load);
          const far = loadFar(base, force);
          addArrow(
            boardPoint(currDir, x, force < 0 ? base : far),
            boardPoint(currDir, x, force < 0 ? far : base),
            color,
          );
          if (showDimensions) {
            addTextLabel(`${Math.abs(force) / 1000} kN`, boardPoint(currDir, x, far + loadLabelOffset), color);
          }
        }

        for (const load of selectedLine) {
          if (!load.direction || load.positions.length < 2 || load.values.length < 2) continue;
          const dc = classify_direction(load.direction);
          const currDir: GraphDirection = dc === "y" ? "xy" : "xz";
          const start = load.positions[0].x;
          const end = load.positions[1].x;
          const startValue = load.values[0];
          const endValue = load.values[1];
          const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
          const base = loadBase(currDir, load);
          const stepCount = Math.max(
            1,
            Math.round(Math.abs(end - start) / isometric_3d.load.distributedHorizontalStep),
          );
          const envelope: THREE.Vector3[] = [];
          const zeroFraction = startValue * endValue < 0 ? startValue / (startValue - endValue) : null;

          for (let step = 0; step <= stepCount; step++) {
            const fraction = step / stepCount;
            const x = start + (end - start) * fraction;
            const value = startValue + (endValue - startValue) * fraction;
            const far = Math.abs(value) < 1e-9 ? base : loadFar(base, value);
            if (Math.abs(value) >= 1e-9) {
              addArrow(
                boardPoint(currDir, x, value < 0 ? base : far),
                boardPoint(currDir, x, value < 0 ? far : base),
                color,
              );
            }
            if (zeroFraction !== null && (step - 1) / stepCount < zeroFraction && zeroFraction < fraction) {
              envelope.push(boardPoint(currDir, start + (end - start) * zeroFraction, base));
            }
            envelope.push(boardPoint(currDir, x, far));
          }
          addLine(envelope, color);
          addLine([boardPoint(currDir, start, base), boardPoint(currDir, end, base)], color);
          if (showDimensions) {
            const addLineLoadLabel = (x: number, value: number) => {
              const far = Math.abs(value) < 1e-9 ? base : loadFar(base, value);
              addTextLabel(`${Math.abs(value) / 1000} kN/m`, boardPoint(currDir, x, far + loadLabelOffset), color);
            };
            addLineLoadLabel(start, startValue);
            if (Math.abs(startValue - endValue) >= 1) addLineLoadLabel(end, endValue);
          }
        }

        for (const load of selectedAxialLine) {
          if (load.positions.length < 2 || load.values.length < 2) continue;
          const start = load.positions[0].x;
          const end = load.positions[1].x;
          const startValue = load.values[0];
          const endValue = load.values[1];
          const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
          const stepCount = Math.max(1, Math.round(Math.abs(end - start) / isometric_3d.load.distributedVerticalStep));

          for (let step = 1; step <= stepCount; step++) {
            const fraction = step / stepCount;
            const x = start + (end - start) * fraction;
            const value = startValue + (endValue - startValue) * fraction;
            if (Math.abs(value) < 1e-9) continue;
            const length = isometric_3d.load.minimumAmplitude + Math.abs(value) * verticalScale;
            const fromX = value < 0 ? x : x + length;
            const toX = value < 0 ? x + length : x;
            addArrow(toWorld(fromX, 0, 0), toWorld(toX, 0, 0), color);
          }
        }

        // skip repeats: loads at the same point would overdraw the lines, making them look darker/heavier
        const drawnEccentricities = new Set<string>();
        for (const load of selectedVertical) {
          const pos = load.positions?.[0];
          const force = force_from_point_load(load);
          if (!Number.isFinite(force) || Math.abs(force) < 1e-9) continue;
          const x = pos?.x ?? 0;
          const path = eccentricityOffsetPath(pos?.y ?? 0, pos?.z ?? 0);
          const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
          const key = `${x}|${pos?.y ?? 0}|${pos?.z ?? 0}|${color}`;
          if (path.length < 2 || drawnEccentricities.has(key)) continue;
          drawnEccentricities.add(key);
          addLine(path.map((p) => toWorld(x, p.y, p.z)), color, true);
        }

        for (const load of selectedVertical) {
          const pos = load.positions?.[0];
          const x = pos?.x ?? 0;
          const color = loadcases_getColor(projectData, load.lcaseGuid).toHex();
          const force = force_from_point_load(load);
          if (!Number.isFinite(force) || Math.abs(force) < 1e-9) continue;
          const length = isometric_3d.load.minimumAmplitude + Math.abs(force) * verticalScale;
          const fromX = force < 0 ? x : x + length;
          const toX = force < 0 ? x + length : x;
          const y = pos?.y ?? 0;
          const z = pos?.z ?? 0;
          addArrow(toWorld(fromX, y, z), toWorld(toX, y, z), color);
          if (showDimensions) {
            addTextLabel(`${Math.abs(force) / 1000} kN`, toWorld((fromX + toX) / 2, y, z), color);
          }
        }
      }
    }

    // fixed isometric orthographic camera: looks at the column center, positioned along the
    // standard isometric direction, never orbited by the user (see applyView below for the
    // screen-space pan/zoom laid on top of it).
    const boardDepth = (cardboardDistance + resultBoardWidth(resultBoardLaneWidth)) * scaleYZ;
    const maxBoardSpan = Math.max(halfY * 2 + boardDepth, halfZ * 2 + boardDepth);
    const target = new THREE.Vector3(boardDepth / 2, height / 2, boardDepth / 2);
    const camDist = 10;
    const camPos = new THREE.Vector3(
      camDist * Math.sin(ISO_YAW) * Math.cos(ISO_PITCH),
      camDist * Math.sin(ISO_PITCH),
      camDist * Math.cos(ISO_YAW) * Math.cos(ISO_PITCH),
    ).add(target);

    const viewSize = Math.max(height, maxBoardSpan) * 1.6 + 1;
    const aspect = width / heightPx;
    const camera = new THREE.OrthographicCamera(
      (-viewSize * aspect) / 2,
      (viewSize * aspect) / 2,
      viewSize / 2,
      -viewSize / 2,
      0.1,
      1000,
    );
    camera.position.copy(camPos);
    camera.lookAt(target);

    const labelLayer = document.createElement("div");
    Object.assign(labelLayer.style, {
      position: "absolute",
      inset: "0",
      overflow: "hidden",
      pointerEvents: "none",
    });
    container.appendChild(labelLayer);
    const renderedLabels = textLabels.map((label) => {
      const element = document.createElement("span");
      if (label.runs) {
        // a zero-size anchor at the label position, each run placed from it in pixels and
        // bottom-aligned on it, exactly as the SVG drawer places the same runs
        for (const run of label.runs) {
          const runElement = document.createElement("span");
          runElement.textContent = run.text;
          Object.assign(runElement.style, {
            position: "absolute",
            left: `${run.horiOffset}px`,
            top: `${run.vertOffset}px`,
            color: run.color,
            fontSize: `${run.fontSize}px`,
            transform: "translateY(-100%)",
          });
          element.appendChild(runElement);
        }
      } else {
        element.textContent = label.text;
      }
      Object.assign(element.style, {
        position: "absolute",
        color: label.color,
        fontSize: `${label.fontSize}px`,
        fontWeight: label.fontWeight,
        lineHeight: "1",
        whiteSpace: "nowrap",
        transform: label.runs ? "none" : "translate(-50%, -50%)",
      });
      labelLayer.appendChild(element);
      return { ...label, element };
    });
    const positionTextLabels = (labelWidth: number, labelHeight: number) => {
      for (const label of renderedLabels) {
        const projected = label.position.clone().project(camera);
        const labelX = (projected.x * 0.5 + 0.5) * labelWidth;
        label.element.style.left = `${rightHand ? labelWidth - labelX : labelX}px`;
        label.element.style.top = `${(-projected.y * 0.5 + 0.5) * labelHeight}px`;
        label.element.style.display = Math.abs(projected.z) <= 1 ? "block" : "none";
      }
      // Origin sits at the midpoint between the column face and the near edge of each result
      // board (i.e. where a line down from the column meets the closest line of each
      // cardboard), not out past the far edge of the boards - keeps the triad close to the
      // column instead of stranded in empty space below/beside the boards.
      const originHalfY = halfY / scaleYZ + cardboardDistance / 2;
      const originHalfZ = halfZ / scaleYZ + cardboardDistance / 2;
      const projectedOrigin = toWorld(0, originHalfY, originHalfZ).project(camera);
      props.onAxisOriginScreenChange?.({
        x: (rightHand ? 0.5 - projectedOrigin.x * 0.5 : projectedOrigin.x * 0.5 + 0.5) * labelWidth,
        y: (-projectedOrigin.y * 0.5 + 0.5) * labelHeight,
      });
    };

    renderer.render(scene, camera);
    positionTextLabels(width, heightPx);

    /**
     * Folds the user's screen-space pan/zoom into the orthographic camera.
     *
     * `camera.zoom` scales about the centre of the viewport, so it covers the zoom and the part of
     * the pan that a centred zoom implies; whatever translation is left over is applied with
     * `setViewOffset`, which slides the rendered window over a same-sized virtual image - i.e. it
     * is a pure pixel shift. The window moves opposite to the content, hence the negation.
     */
    const applyView = () => {
      const w = container.clientWidth || 300;
      const h = container.clientHeight || 300;
      const { zoom, pan } = viewRef.current;
      const extraX = pan[0] - (w / 2) * (1 - zoom);
      const extraY = pan[1] - (h / 2) * (1 - zoom);
      camera.zoom = zoom;
      if (extraX === 0 && extraY === 0) camera.clearViewOffset();
      else camera.setViewOffset(w, h, rightHand ? extraX : -extraX, -extraY, w, h);
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
      positionTextLabels(w, h);
    };

    applyViewRef.current = applyView;
    applyView();
    // A rebuild means the drawing changed, so return to the fitted view. The first build is the
    // drawing simply appearing and has no user view to reset.
    if (hasBuiltOnce.current) contentChanged();
    hasBuiltOnce.current = true;

    const handleResize = () => {
      const w = container.clientWidth || 300;
      const h = container.clientHeight || 300;
      renderer.setSize(w, h);
      const a = w / h;
      camera.left = (-viewSize * a) / 2;
      camera.right = (viewSize * a) / 2;
      // the leftover pan is measured in pixels, so it has to be recomputed for the new size
      applyView();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      applyViewRef.current = null;
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
          obj.geometry?.dispose();
          const mat = obj.material;
          if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
          else mat?.dispose();
        }
      });
      renderer.dispose();
      if (container.contains(labelLayer)) container.removeChild(labelLayer);
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, [
    projectData,
    props.results,
    props.selectedLcombGuid,
    props.graphPointsMode,
    scaleX,
    scaleYZ,
    cardboardDistance,
    resultBoardLaneWidth,
    rightHand,
    resultBoardGraphAmplitude,
    resultBoardClearance,
    resultBoardHeaderOffsetsLeft,
    resultBoardHeaderOffsetsRight,
    offsetLoadsFromColumn,
    startCardboardsAtOuterCenterLine,
    show3dLoadsOnOppositeSide,
    halfY,
    halfZ,
    height,
    outlineColor,
    columnSideColor,
    columnTopColor,
    shadedColumnColor,
    showDimensions,
    showCardboard,
    showReinforcement,
    showBuckling,
    loadColumnSpacing,
    fakeSilhouette3d,
    geometryDebug3d,
    debugExtrusion3d,
    debugExtrusionNormals3d,
    debugExtrusionNormalsCenter3d,
    debugExtrusionNormalsRadius3d,
    drawFaces3d,
    shadeFaces3d,
    ngLight,
    worldLight,
    hiddenLines3d,
    hiddenLineGrey3d,
    contentChanged,
    props.onAxisOriginScreenChange,
  ]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <ViewportButtons>
        <PanZoomResetButton panZoom={panZoom} />
      </ViewportButtons>
      {/* the effect above owns this element's children (it clears and refills it), so the
          buttons have to live outside it */}
      <div
        style={{ position: "relative", width: "100%", height: "100%", ...panZoom.gestureStyle }}
        {...panZoom.containerProps}
      />
    </div>
  );
};
